import * as _ from 'lodash';
import { useState } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { put, remove } from '../../../../../redux/api';
import useOrganizationSettings from '../../../shared/useOrganizationSettings';
import { isLdapConfigurationValid } from '../SSOConfiguration/LDAP/ldapConfigHelpers';
import type { LDAPStrategy, SamlStrategy } from '../../../../../types';
import type { AuthenticationSettingsWithPasswords } from '../types';

const useAuthenticationSettings = (organizationId: number) => {
  const addToast = useAddToast();
  const [isLDAPEdited, setIsLDAPEdited] = useState<boolean>(false);
  const [isSAMLEdited, setIsSAMLEdited] = useState<boolean>(false);
  const [hasNewConfig, setHasNewConfig] = useState<boolean>(false);
  // Even though we use useOrganizationSettings, we still need to keep track of a separate state for isSaving because
  // useOrganizationSettings does not account for the saving state of the LDAP and SAML settings.
  const [isSaving, setIsSaving] = useState(false);
  const {
    onUpdateSettings: setResolve,
    settings,
    isLoading,
    errors,
    onSaveSettingsWithOverride,
    isSaveButtonDisabled,
  } = useOrganizationSettings<AuthenticationSettingsWithPasswords>({
    settingsSection: 'authentication',
    endpoint: `/api/organizations/${organizationId}/settings/authentication`,
  });

  const onUpdateSettings = (updatedSettings: Partial<AuthenticationSettingsWithPasswords>) => {
    setResolve({
      ...settings,
      ...updatedSettings,
    });
  };

  const onSaveSAMLSettings = async () => {
    const { entryPoint, cert } = settings?.saml || {};
    if ((entryPoint && !cert) || (!entryPoint && cert)) {
      const errorMessage = entryPoint
        ? 'SAML configuration may not be saved: Only the single sign-on URL was provided but not a certificate.'
        : 'SAML configuration may not be saved: Only the certificate was provided but not a single sign-on URL.';

      throw new Error(errorMessage);
    }
    if (entryPoint) {
      await put(`/api/organizations/${organizationId}/saml`, settings?.saml);
    } else {
      await remove(`/api/organizations/${organizationId}/saml`);
    }
    setIsSAMLEdited(false);
    setHasNewConfig(false);
  };

  const onSaveLDAPSettings = async () => {
    const { ldap } = settings || {};
    if (ldap?.enabled && !isLdapConfigurationValid(ldap, isLDAPEdited)) {
      throw new Error('Required fields are missing. Please fill out all fields before saving.');
    }
    await put('/api/ldap', settings?.ldap);
    setIsLDAPEdited(false);
  };

  const onSaveSettings = async () => {
    try {
      setIsSaving(true);
      if (isSAMLEdited) {
        await onSaveSAMLSettings();
      }
      if (isLDAPEdited) {
        await onSaveLDAPSettings();
      }
      // onSaveSettingsWithOverride will trigger a toast message on success.
      await onSaveSettingsWithOverride(
        _.pick(settings, ['disableInvite', 'subdomain']) as AuthenticationSettingsWithPasswords,
      );
    } catch (error: unknown) {
      addToast({ type: 'error', content: (error as Error).message });
    } finally {
      setIsSaving(false);
    }
  };

  const onDeleteSAML = async () => {
    try {
      await remove(`/api/organizations/${organizationId}/saml`);
    } catch (error: unknown) {
      addToast({ type: 'error', content: `Failed to delete SAML configuration.` });
    }
  };

  const onUpdateLDAP = (updatedLDAP: Partial<LDAPStrategy>, isSilent: boolean = false) => {
    if (!isSilent) {
      setIsLDAPEdited(true);
    }
    setResolve({
      ...settings,
      ldap: {
        ...settings?.ldap,
        ...updatedLDAP,
      },
    } as AuthenticationSettingsWithPasswords);
  };

  const onUpdateSAML = (updatedSAML: Partial<SamlStrategy>, isSilent: boolean = false) => {
    if (!isSilent) {
      setIsSAMLEdited(true);
    }
    if (!settings?.saml.entryPoint && !settings?.saml.cert && (updatedSAML.entryPoint || updatedSAML.cert)) {
      // The SAML config was empty, but now it's not. Set the hasNewConfig property to true so that we know we need to
      // hook into the "Save Changes" action.
      setHasNewConfig(true);
    }
    setResolve({
      ...settings,
      saml: {
        ...settings?.saml,
        ...updatedSAML,
      },
    } as AuthenticationSettingsWithPasswords);
  };

  return {
    settings,
    isLoading: isLoading.fetch,
    errors,
    onUpdateSettings,
    onSaveSettings,
    isSaveButtonDisabled: isSaveButtonDisabled && !isLDAPEdited && !isSAMLEdited,
    isLDAPEdited,
    isSAMLEdited,
    isSaving,
    onDeleteSAML,
    onUpdateLDAP,
    onUpdateSAML,
    hasNewConfig,
  };
};

export default useAuthenticationSettings;
